import { type ChangeEvent, type FocusEvent, type KeyboardEvent, useEffect, useState } from "react";

type Props<T extends string | number> = {
    name?: string;
    className?: string;
    placeholder?: string | null;
    value?: T | null;
    defaultValue?: T | null;
    onChange?: (value: T | null) => void;
    type?: "text" | "textarea" | "number" | "date" | "color";
    autoFocus?: boolean;
    onFocus?: (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    onKeyDown?: (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    required?: boolean;
};

export default function UiInput<T extends string | number = string | number>(props: Props<T>) {
    const type = props.type ?? "text";

    const normalize = (text: string): T | null => {
        const trimmed = text.trim();
        if ("" === trimmed) {
            return null;
        }

        return ("number" === type ? +trimmed : trimmed) as T;
    };

    // The field keeps the raw text, trailing spaces included: trimming it while it is controlled
    // would erase the space just typed before the next letter lands. Only what leaves is normalized.
    const [ text, setText ] = useState<string>((props.defaultValue ?? "").toString());

    useEffect(() => {
        const incoming = (props.defaultValue ?? "").toString();
        if (normalize(incoming) !== normalize(text)) {
            setText(incoming);
        }
    }, [ props.defaultValue ]);

    useEffect(() => {
        const timer = setTimeout(() => {
            const value = normalize(text);
            if (normalize((props.defaultValue ?? "").toString()) === value) {
                return;
            }

            props.onChange?.(value);
        }, 500);

        return () => clearTimeout(timer);
    }, [ text ]);

    const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setText(e.target.value ?? "");
    };

    const Tag = "textarea" === type ? "textarea" : "input";

    return (
        <Tag
            name={props.name}
            className={`form-control ${props.className ?? ""}`}
            placeholder={props.placeholder ?? ""}
            type={type}
            value={text}
            autoFocus={props.autoFocus ?? false}
            onFocus={props.onFocus}
            onKeyDown={props.onKeyDown}
            onChange={handleChange}
            required={props.required ?? false}
        />
    );
}
